import * as z from "zod";
import ContractEndTypeEnum from "@/constants/enums/ContractEndTypeEnum";
import ContractStatusEnum from "@/constants/enums/ContractStatusEnum";

import {
  TransportResponsibleType,
  CurrencyCode,
  WeightResponsibleType,
  ContractCompanySpecificService,
  ContractDeviation,
} from "@/types";

const contractEndTypeValues = Object.values(ContractEndTypeEnum) as [string, ...string[]];
const transportResponsible: z.ZodType<TransportResponsibleType> = z.any();
const currencyType: z.ZodType<CurrencyCode> = z.any();
const weightResponsibleType: z.ZodType<WeightResponsibleType> = z.any();
const companySpecificServicesType: z.ZodType<ContractCompanySpecificService> = z.any();
const contractDeviationType: z.ZodType<ContractDeviation> = z.any();

const ContractBaseSchema = z.object({
  name: z.string().min(1, "required"),
  description: z.string(),
  contractBuyers: z.array(z.number()),
  contractSellers: z.array(z.number()),
  contractDate: z.date(),
  contractStartDate: z.date(),
  companySpecificProductId: z.number(),
  contractStatus: z.nativeEnum(ContractStatusEnum),
  useTransportServices: z.boolean(),
  agreedBuyerPricePrUnit: z.preprocess(
    (val) => (val === "" ? NaN : Number(val)),
    z.number({
      required_error: "required",
      invalid_type_error: "expectedNumber",
    })
  ),
  agreedSellerPricePrUnit: z.preprocess(
    (val) => (val === "" ? NaN : Number(val)),
    z.number({
      required_error: "required",
      invalid_type_error: "expectedNumber",
    })
  ),
  allSelectedServices: z.array(z.number()),
  selectedServices: z.array(z.number()),
  companySpecificServices: z.array(companySpecificServicesType),
  invoiceIntervalId: z.number(),
  invoiceDueDateId: z.number(),
  transportResponsible: transportResponsible,
  currencyType: currencyType,
  comment: z.string().optional(),
  priceUnitId: z.number(),
  transportFromAddress: z.any(),
  transportToAddress: z.any(),
  attachments: z.any(),
  automaticSalesApproval: z.boolean(),
  weightResponsible: weightResponsibleType,
  contractManagerId: z.string(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  contactCounterpartyEmail: z.string().nullable(),
  contactCounterpartyPhone: z.string().nullable(),
  tradeAtOwnRisk: z.boolean(),
  isPaymentReceiverInverted: z.boolean(),
  contractDeviations: z.array(contractDeviationType),
  template: z.boolean().optional(),
  templateName: z.string().optional().nullable(),
  poNumber: z.string().optional().nullable(),
  skipInvoicing: z.boolean(),
  indexRegulated: z.boolean(),
});

export const EditContractSchema = ContractBaseSchema.extend({
  contractType: z.enum(contractEndTypeValues),

  contractEndDate: z.coerce
    .date({
      required_error: "validation:endDateRequired",
      invalid_type_error: "validation:endDateRequired",
    })
    .nullable()
    .optional(),

  amount: z
    .string({
      required_error: "validation:amountRequired",
      invalid_type_error: "validation:amountRequired",
    })
    .min(1, "validation:amountRequired")
    .nullable(),
  amountUnitId: z.string({
    required_error: "validation:amountUnitRequried",
    invalid_type_error: "validation:amountUnitRequried",
  }),
}).superRefine((data, ctx) => {
  if (data.contractType === ContractEndTypeEnum.DATEPERIOD) {
    if (data.contractEndDate == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "validation:endDateRequired",
        path: ["contractEndDate"],
      });
    }
  } else if (data.contractType === ContractEndTypeEnum.AMOUNT) {
    if (data.amount == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "validation:amountRequired",
        path: ["amount"],
      });
    }
    if (data.amountUnitId == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "validation:amountUnitRequried",
        path: ["amountUnitId"],
      });
    }
  } else {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Invalid contractType",
      path: ["contractType"],
    });
  }
});

export type EditContractInput = z.infer<typeof EditContractSchema>;

const DifferentAddress = z.object({
  address: z
    .string({
      required_error: "addressRequired",
      invalid_type_error: "addressRequired",
    })
    .min(1, { message: "addressRequired" }),
  countryId: z
    .string({
      required_error: "countryRequired",
      invalid_type_error: "countryRequired",
    })
    .min(1, { message: "countryRequired" }),
  postCode: z
    .string({
      required_error: "postCodeRequired",
      invalid_type_error: "postCodeRequired",
    })
    .min(1, { message: "postCodeRequired" }),
  place: z
    .string({
      required_error: "placeRequired",
      invalid_type_error: "placeRequired",
    })
    .min(1, { message: "placeRequired" }),
  addressType: z.any().optional(),
});

export const DifferentAddressSchema = DifferentAddress;
export type DifferentAddressInput = z.infer<typeof DifferentAddressSchema>;

type UnionKeys<T> = T extends T ? keyof T : never;
export type EditContractFields = UnionKeys<EditContractInput>;
